import {
  Boxes,
  ChevronRight,
  CircleHelp,
  Database,
  LayoutDashboard,
  Map,
  Menu,
  PackageCheck,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { useState } from "react";
import type { DataQuality, UserProfile, WarehouseDataset } from "../types";

export type PageId = "overview" | "products" | "warehouse" | "batches" | "replenishment" | "assistant" | "profile";

const navItems: Array<{ id: PageId; label: string; eyebrow: string; icon: typeof LayoutDashboard }> = [
  { id: "overview", label: "今日决策", eyebrow: "01", icon: LayoutDashboard },
  { id: "products", label: "品项趋势", eyebrow: "02", icon: TrendingUp },
  { id: "warehouse", label: "空间配位", eyebrow: "03", icon: Map },
  { id: "batches", label: "批次效期", eyebrow: "04", icon: PackageCheck },
  { id: "replenishment", label: "补货节奏", eyebrow: "05", icon: RefreshCw },
  { id: "assistant", label: "决策智能体", eyebrow: "AI", icon: Sparkles },
];

const pageMeta: Record<PageId, { title: string; section: string }> = {
  overview: { title: "今日现场信号与决策摘要", section: "经营总览" },
  products: { title: "需求趋势与单品回归证据", section: "商品分析" },
  warehouse: { title: "全库位空间与拣货路径", section: "空间配位" },
  batches: { title: "批次效期与客户约束分配", section: "库存批次" },
  replenishment: { title: "需求预测与补货节奏", section: "补货计划" },
  assistant: { title: "可解释的经营决策方案", section: "决策智能体" },
  profile: { title: "个人资料与工作空间", section: "账户设置" },
};

interface ShellProps {
  page: PageId;
  children: React.ReactNode;
  dataset: WarehouseDataset;
  quality: DataQuality;
  profile: UserProfile;
  onNavigate: (page: PageId) => void;
  onImport: () => void;
  onOpenSupport: () => void;
}

export function Shell({ page, children, dataset, quality, profile, onNavigate, onImport, onOpenSupport }: ShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const meta = pageMeta[page];
  const datasetLabel =
    dataset.meta.source === "api"
      ? "后端数据已连接"
      : dataset.meta.source === "file"
        ? "已导入真实数据"
        : dataset.meta.source === "sample"
          ? "旧版演示数据"
          : "尚未接入数据";

  function navigate(next: PageId) {
    onNavigate(next);
    setMobileOpen(false);
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="brand">
          <div className="brand-mark" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div>
            <div className="brand-name">仓序</div>
            <div className="brand-sub">WAREHOUSE INTELLIGENCE</div>
          </div>
          <button className="icon-button sidebar-close" onClick={() => setMobileOpen(false)} aria-label="关闭菜单">
            <X size={18} />
          </button>
        </div>

        <div className="workspace-caption">WORKSPACES</div>
        <nav className="primary-nav" aria-label="主导航">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={`nav-item ${page === item.id ? "is-active" : ""}`}
                key={item.id}
                onClick={() => navigate(item.id)}
                aria-current={page === item.id ? "page" : undefined}
              >
                <span className="nav-icon"><Icon size={18} strokeWidth={1.7} /></span>
                <span className="nav-label">{item.label}</span>
                <span className="nav-index">{item.eyebrow}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-spacer" />
        <div className="dataset-card">
          <div className="dataset-icon"><Database size={17} /></div>
          <div className="dataset-copy">
            <span className={`status-dot status-${quality.level}`} />
            <strong>{datasetLabel}</strong>
            <small>{dataset.meta.source === "empty" ? "等待后端或文件数据" : `${dataset.meta.warehouse} · ${quality.days} 天`}</small>
          </div>
        </div>
        <button className={`profile-chip ${page === "profile" ? "is-active" : ""}`} onClick={() => navigate("profile")}>
          {profile.avatarDataUrl ? (
            <img src={profile.avatarDataUrl} alt="" />
          ) : (
            <span className="avatar-fallback">{profile.name?.slice(0, 1) || "仓"}</span>
          )}
          <span>
            <strong>{profile.name || "完善个人资料"}</strong>
            <small>{profile.company || "仓序工作空间"}</small>
          </span>
          <ChevronRight size={15} />
        </button>
      </aside>

      {mobileOpen && <button className="sidebar-scrim" aria-label="关闭菜单" onClick={() => setMobileOpen(false)} />}

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-left">
            <button className="icon-button menu-button" aria-label="打开菜单" onClick={() => setMobileOpen(true)}>
              <Menu size={20} />
            </button>
            <div className="page-heading">
              <span>{meta.section}</span>
              <h1>{meta.title}</h1>
            </div>
          </div>
          <div className="topbar-actions">
            <div className={`quality-chip quality-${quality.level}`}>
              <span className="signal-dot" />
              {quality.level === "good" ? "数据可用于决策" : quality.level === "limited" ? "有限数据判断" : "证据不足"}
            </div>
            <button className="ghost-button topbar-help" aria-label="打开仓储客服" title="仓储客服" onClick={onOpenSupport}>
              <CircleHelp size={17} />
            </button>
            <button className="primary-button compact" onClick={onImport}>
              <Upload size={16} />
              导入数据
            </button>
          </div>
        </header>

        <main className={`page page-${page}`} key={page}>
          {children}
        </main>

        <nav className="mobile-nav" aria-label="移动端导航">
          {navItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} className={page === item.id ? "is-active" : ""} onClick={() => navigate(item.id)}>
                <Icon size={19} strokeWidth={1.7} />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button className={page === "profile" ? "is-active" : ""} onClick={() => navigate("profile")}>
            <UserRound size={19} strokeWidth={1.7} />
            <span>我的</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
